import { setAutoFreeze } from 'immer'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { isDevtoolsEnabled } from 'src/store/utils/isDevtoolsEnabled'
import type { Booster, InventoryItem } from './types/domain'
import { getOldestAutoApplyBooster, isClaimed, isBonus, isBooster } from './types/domain'
import { selectFreeBets } from './utils/selectFreeBets'

setAutoFreeze(false)

interface InventoryState {
  items: InventoryItem[]
  isInventoryPanelOpen: boolean
  activeBonusId: string | null
  activatedItemIds: string[]
}

export interface InventoryActions {
  setInventoryItems: (items: InventoryItem[]) => void
  addInventoryItem: (item: InventoryItem) => void
  updateInventoryItem: (item: InventoryItem) => void
  claimItem: (itemId: string) => void // Claim a claimable item
  revertClaimItem: (itemId: string) => void // Revert failed claim
  activateItem: (itemId: string) => void // Activate a manual item for use
  deactivateItem: (itemId: string) => void // Remove item from activated list
  closeInventoryPanel: () => void
  toggleInventoryPanel: () => void
  reset: () => void
}

type InventoryStore = InventoryState & {
  actions: InventoryActions
}

export const useInventoryStore = create<InventoryStore>()(
  devtools(
    immer((set) => ({
      items: [],
      isInventoryPanelOpen: false,
      activeBonusId: null,
      activatedItemIds: [],
      actions: {
        setInventoryItems: (items) => {
          set(
            (state) => {
              state.items = items
            },
            undefined,
            'setInventoryItems'
          )
        },
        addInventoryItem: (item) => {
          set(
            (state) => {
              const filtered = state.items.filter((i) => !(i.id === item.id))
              state.items = [...filtered, item]
            },
            undefined,
            'addInventoryItem'
          )
        },
        updateInventoryItem: (item: InventoryItem) => {
          set(
            (state) => {
              const index = state.items.findIndex((i) => i.id === item.id)
              if (index !== -1) {
                state.items[index] = item

                // If this item was the active bonus and it's now cancelled/not claimed, clear it
                if (state.activeBonusId === item.id && item.status !== 'claimed') {
                  state.activeBonusId = null
                }

                // Remove from activated items if it's no longer claimed
                if (item.status !== 'claimed') {
                  state.activatedItemIds = state.activatedItemIds.filter((id) => id !== item.id)
                }
              } else {
                console.warn(`Item with id ${item.id} not found in inventory`)
              }
            },
            undefined,
            'updateInventoryItem'
          )
        },
        claimItem: (itemId) => {
          set(
            (state) => {
              const item = state.items.find((i) => i.id === itemId)
              if (item && item.status === 'claimable') {
                // Optimistically update to claimed status
                item.status = 'claimed'
                item.claimedAt = new Date().toISOString()
              }
            },
            undefined,
            'claimItem'
          )
        },
        revertClaimItem: (itemId) => {
          set(
            (state) => {
              const item = state.items.find((i) => i.id === itemId)
              if (item && item.status === 'claimed') {
                // Revert back to claimable status
                item.status = 'claimable'
                item.claimedAt = undefined
              }
            },
            undefined,
            'revertClaimItem'
          )
        },
        activateItem: (itemId) => {
          set(
            (state) => {
              const item = state.items.find((i) => i.id === itemId)

              if (item) {
                if (isBonus(item)) {
                  if (!state.activatedItemIds.includes(itemId)) {
                    state.activatedItemIds.push(itemId)
                  }

                  state.activeBonusId = itemId
                }
                // Check if it's a booster item with manual activation
                else if (isBooster(item) && item.activationType === 'manual') {
                  if (!state.activatedItemIds.includes(itemId)) {
                    state.activatedItemIds.push(itemId)
                  }
                }
              }
            },
            undefined,
            'activateItem'
          )
        },
        deactivateItem: (itemId) => {
          set(
            (state) => {
              // Remove from activated items
              state.activatedItemIds = state.activatedItemIds.filter((id) => id !== itemId)

              // If it was the active bonus, clear that too
              if (state.activeBonusId === itemId) {
                state.activeBonusId = null
              }
            },
            undefined,
            'deactivateItem'
          )
        },
        closeInventoryPanel: () => {
          set(
            (state) => {
              state.isInventoryPanelOpen = false
            },
            undefined,
            'closeInventoryPanel'
          )
        },
        toggleInventoryPanel: () => {
          set(
            (state) => {
              state.isInventoryPanelOpen = !state.isInventoryPanelOpen
            },
            undefined,
            'toggleInventoryPanel'
          )
        },
        reset: () => {
          set(
            (state) => {
              state.items = []
              state.isInventoryPanelOpen = false
              state.activeBonusId = null
              state.activatedItemIds = []
            },
            undefined,
            'resetInventory'
          )
        }
      }
    })),
    { name: 'InventoryStore', enabled: isDevtoolsEnabled() }
  )
)

export const useInventoryActions = () => useInventoryStore((state) => state.actions)

export const useGetAutoBooster = (gameId?: string) =>
  useInventoryStore((state) => {
    const items = gameId
      ? state.items
          .filter(
            (item) => item.applicableGames.length === 0 || item.applicableGames.includes(gameId)
          )
          .filter(isClaimed)
      : state.items

    return getOldestAutoApplyBooster(items)
  })

export const useInventoryItems = (_gameId?: string) =>
  useInventoryStore((state) => {
    const inventoryItems = state.items.filter((item) => isClaimed(item))
    return inventoryItems
  })

export const useInventoryItem = (id: string) =>
  useInventoryStore((state) => state.items.find((item) => item.id === id) ?? null)

export const useInventoryItemCount = () =>
  useInventoryStore((state) => state.items.filter((item) => isClaimed(item)).length)

export const useInventoryPanelState = () => useInventoryStore((state) => state.isInventoryPanelOpen)

export const useGetActiveBonus = (gameId?: string) =>
  useInventoryStore((state) => {
    if (!state.activeBonusId) {
      return null
    }

    const activeBonus = state.items.find((item) => item.id === state.activeBonusId && isBonus(item))

    if (!activeBonus || !isBonus(activeBonus)) return null

    // Filter by game applicability if gameId is provided
    if (
      gameId &&
      activeBonus.applicableGames.length > 0 &&
      !activeBonus.applicableGames.includes(gameId)
    ) {
      return null
    }

    return activeBonus
  })

// --- Free-bet specific selectors (used by auto-claim, the grant banner and the sidebar) ---

/** Claimable free bets applicable to the game — the auto-claim / grant-banner input. */
export const useClaimableFreeBetsForGame = (gameId?: string) =>
  useInventoryStore((state) => selectFreeBets(state.items, { status: 'claimable', gameId }))

/** Claimed (ready-to-use) free bets applicable to the game — the sidebar / count input. */
export const useClaimedFreeBetsForGame = (gameId?: string) =>
  useInventoryStore((state) => selectFreeBets(state.items, { status: 'claimed', gameId }))

/** Count of claimed free bets the player holds (applicable to the game if given). */
export const useFreeBetCount = (gameId?: string) =>
  useInventoryStore((state) => selectFreeBets(state.items, { status: 'claimed', gameId }).length)

/** Claimed boosters — the sidebar booster section. */
export const useClaimedBoosters = (): Booster[] =>
  useInventoryStore((state) =>
    state.items.filter((item): item is Booster => isBooster(item) && isClaimed(item))
  )

/** Total rewards shown in the sidebar: free bets for this game + all claimed boosters. */
export const useHeldRewardsCount = (gameId?: string) =>
  useInventoryStore(
    (state) =>
      selectFreeBets(state.items, { status: 'claimed', gameId }).length +
      state.items.filter((item) => isBooster(item) && isClaimed(item)).length
  )
